export const CHANGE_TYPES = {
  ADDED: "added",
  EDITED: "edited",
  REMOVED: "removed"
};

export const CHANGE_KINDS = {
  SCALAR: "scalar",
  TEXT: "text",
  LIST: "list",
  TABLE: "table"
};

// A tab's changes are shown as tables: FIELDS rows are "field: was → now" (or an added/removed list
// value); RECORDS rows are the changed rows of a table field or knowledge table, cell by cell.
export const SECTION_KINDS = {
  FIELDS: "fields",
  RECORDS: "records"
};

export const DIFF_OPS = {
  SAME: "same",
  DELETED: "del",
  INSERTED: "ins"
};

export const SECRET_MASK = "••••••••";
export const EMPTY_VALUE = "—";
export const EMPTY_LANGUAGE_VALUE = "(empty)";
export const BOOLEAN_LABELS = { true: "On", false: "Off" };
// Longer values are diffed word by word instead of shown twice side by side.
export const LONG_TEXT_LENGTH = 80;
// Above this many words per side the word diff falls back to whole-text before/after.
export const MAX_DIFF_WORDS = 1500;
export const PATH_SEPARATOR = " › ";
export const LANGUAGE_SEPARATOR = " · ";

export const INSTANCE_FIELD_LABELS = {
  NAME: "Name",
  ENABLED: "Enabled",
  SUGGESTIONS: "Suggestions"
};

// Knowledge rows are shown by the first of these columns they have.
export const ROW_LABEL_FIELDS = ["name", "full_name", "display_name", "title"];
export const ROW_LABEL_FALLBACK_COLUMN = { key: "__row", label: "Row" };

// Review groups in the order the collection pages are listed; `route` is the client route segment.
export const REVIEW_GROUPS = [
  { key: "skills", title: "Skills", itemLabel: "Skill", route: "skills" },
  { key: "building-blocks", title: "Building blocks", itemLabel: "Building block", route: "building-blocks" },
  { key: "integrations", title: "Integrations", itemLabel: "Integration", route: "integrations" },
  { key: "knowledge-sources", title: "Knowledge", itemLabel: "Knowledge source", route: "knowledge-studio" },
  { key: "settings", title: "Settings" }
];

export const KNOWLEDGE_GROUP_KEY = "knowledge-sources";
export const SETTINGS_GROUP_KEY = "settings";

export const SETTINGS_ENTRIES = {
  CHANNEL: { key: "channel-settings", name: "Channel settings", tabLabel: "Settings" },
  LANGUAGES: { key: "languages", name: "Languages", tabLabel: "Languages" }
};

export const KNOWLEDGE_TABLE_ITEM_LABEL = "Knowledge table";
export const KNOWLEDGE_SOURCE_AND_TABLE_ITEM_LABEL = "Knowledge source & Knowledge table";
// A row id longer than this is shown as its first and last characters ("01a163e8…6304").
export const SHORT_ID_HEAD = 8;
export const SHORT_ID_TAIL = 4;

export const REVIEW_GATES = { BUILD: "build", PUBLISH: "publish" };
export const REVIEW_BASES = { BUILD: "build", LIVE: "live" };

export const REVIEW_FEATURE_FLAG = "reviewPendingChanges";

export const REVIEW_TEST_ID = "review-changes";

export const REVIEW_COPY = {
  TITLES: {
    [REVIEW_GATES.BUILD]: "Review changes before build",
    [REVIEW_GATES.PUBLISH]: "Review changes before publishing"
  },
  SUBMIT: {
    [REVIEW_GATES.BUILD]: "Build",
    [REVIEW_GATES.PUBLISH]: "Continue"
  },
  BASE_OPTIONS: {
    [REVIEW_BASES.BUILD]: "Last build",
    [REVIEW_BASES.LIVE]: "Live version"
  },
  LAST_BUILD_UNAVAILABLE: "Unavailable: there's no usable last build yet, so changes are compared with the live version.",
  KNOWLEDGE_BUILD_NOTE: "Knowledge table changes can only be compared with the live version, so they aren't listed here.",
  SHOW_LIVE: "Compare to live version",
  KNOWLEDGE_OMITTED_NOTE: "Knowledge table changes aren't listed because you don't have access to knowledge sources. They're still included.",
  KNOWLEDGE_ERROR_TITLE: "Couldn't load knowledge changes",
  KNOWLEDGE_ERROR_BODY: "Other changes are shown below. Knowledge edits are still included.",
  EMPTY_TITLES: {
    [REVIEW_BASES.BUILD]: "No changes since last build",
    [REVIEW_BASES.LIVE]: "No unpublished changes"
  },
  EMPTY_BODIES: {
    [REVIEW_BASES.BUILD]: "Your draft matches what was last built.",
    [REVIEW_BASES.LIVE]: "Your draft matches the live version."
  },
  ERROR_TITLE: "We couldn't load your changes",
  ERROR_BODY: "You can try again, or continue without reviewing.",
  RETRY: "Try again",
  CONTINUE_ANYWAY: "Continue anyway",
  OPEN_TAB: "Open in a new tab",
  OPEN_IN_EXPLORE: "Open in Explore",
  TAB_SUFFIX: "tab",
  LOADING: "Loading changes...",
  COMPARE_TO: "Compare to",
  CLEAR_ALL: "Clear all",
  SHOW_MORE: "Show more",
  SHOW_LESS: "Show less",
  WAS: "WAS",
  NOW: "NOW",
  COLUMNS: { CHANGE: "Change" },
  EXPAND: "Expand",
  COLLAPSE: "Collapse",
  // "Build 7 changes" on Build; Publish continues to the description step.
  // "Build 7 changes" / "Continue with 7 changes" (Publish continues to its description step).
  submitLabel: (gate, count) => {
    if (!count) return REVIEW_COPY.SUBMIT[gate];
    const changes = `${count} ${count === 1 ? "change" : "changes"}`;
    return gate === REVIEW_GATES.BUILD ? `Build ${changes}` : `Continue with ${changes}`;
  },
  totalChanges: (count) => `${count} ${count === 1 ? "change" : "changes"}`,
  unchanged: (count) => `${count} unchanged`,
  moreRows: (count) => `+ ${count} more rows`,
  unnamedRow: (rowType) => `Unnamed ${rowType}`,
  rowId: (id) => `ID ${id}`
};

// Palette paths for the change badges (Chip backgroundColorPath / textColorPath) and the filters' dots.
export const CHANGE_BADGES = {
  [CHANGE_TYPES.ADDED]: { label: "Added", background: ["success", 200], text: ["success", 700], dot: ["success", 600] },
  [CHANGE_TYPES.EDITED]: { label: "Edited", background: ["info", 200], text: ["info", 700], dot: ["info", 600] },
  [CHANGE_TYPES.REMOVED]: { label: "Removed", background: ["error", 200], text: ["error", 700], dot: ["error", 600] }
};

export const BADGE_COLUMN_WIDTH = 130;
export const LINK_COLUMN_WIDTH = 48;
// Value columns share the table's width. Each is at least wide enough to read a value without heavy wrapping,
// and for its header (HyroTable truncates a header at its column's minimum width, estimated from the label's
// length); a table with more columns than fit scrolls sideways.
export const RECORD_COLUMN_MIN_WIDTH = 180;
export const HEADER_CHAR_WIDTH = 9;
export const HEADER_PADDING = 56;

export const ALL_FILTER = { label: "All", countKey: "all" };

// Change-type filters; several can be selected at once.
export const REVIEW_FILTERS = [
  { value: CHANGE_TYPES.EDITED, label: "Edited", countKey: "edited" },
  { value: CHANGE_TYPES.ADDED, label: "Added", countKey: "added" },
  { value: CHANGE_TYPES.REMOVED, label: "Removed", countKey: "removed" }
];

// Cell text longer than this, or with more lines, is clamped behind "Show more".
export const CLAMP_LENGTH = 120;
export const CLAMP_LINES = 3;
